<template>
	<fragment>
		<div class="map-aside">
			<div v-html="content"></div>
			<ul class="accordion" ref="accordions">
				<accordion
					v-for="(area, areaName) in areas"
					:key="areaName"
					:question="areaName"
					:current-question="selectedArea"
					@click="areaClicked"
				>
					<template v-slot:question>
						{{ areaName }}: {{ area.length }} Location{{ area.length > 1 ? 's' : '' }}
					</template>
					<template v-slot:answer>
						<a
							v-for="location in area"
							:key="location.id"
							:class="{ active: activeMarker && activeMarker.id == location.id}"
							@click="clickMarker(location.id)"
						>
							{{ location.regionName }} — {{ location.title }}
							<span v-if="location.isLab" class="lab"></span>
						</a>
					</template>
				</accordion>
			</ul>
		</div>
		<GmapMap
			ref="map"
			class="map-container"
			:options="{
				streetViewControl: false,
				scaleControl: true,
				mapTypeId: google && google.maps.MapTypeId.ROADMAP,
				fullscreenControl: !notDesktop,
				mapTypeControl: false,
				styles: mapStyles
			}"
			:zoom="notDesktop ? 8 : 10"
			:center="{ lat: 40.9067682, lng: -74.0068838 }"
		>
			<GmapInfoWindow
				:position="activeMarker ? activeMarker.position : null"
				:opened="activeMarker ? activeMarker.isActive : false"
				:options="{
					pixelOffset: google && new google.maps.Size(0, -35)
				}"
				@closeclick="activeMarker.isActive = false"
			>
				<div class="poi-info-window gm-style" v-html="activeMarker && activeMarker.content"></div>
			</GmapInfoWindow>
			<GmapMarker
				v-for="marker in locations"
				:key="marker.id"
				:position="marker.position"
				:clickable="true"
				:icon="{
					url: getLocationIcon(marker.isActive, marker.isLab),
					origin: google && new google.maps.Point(0, 0),
					size: google && new google.maps.Size(17, 25),
				}"
				@click="clickMarker(marker.id)"
			/>
		</GmapMap>
	</fragment>
</template>

<script>
import { notDesktop, phone } from '../site.js';;
import { gmapApi } from 'vue2-google-maps';
import Accordion from './Accordion';
import MapStyles from '../MapStyles.json';

export default {
	props: {
		initialSelectedArea: {
			type: String,
			default: 'all'
		},
		content: String,
		initialLocations: Array
	},

	components: {
		Accordion
	},

	data() {
    // add scss styling as well, mapStyles doesn't do that
		return {
      fullMap: false,
			notDesktop: notDesktop,
			mapStyles: MapStyles,
			selectedArea: this.initialSelectedArea,
			locations: this.initialLocations
		}
	},

	mounted() {
    const fullMap = document.getElementById('full-map')
    if (fullMap) {
      this.fullMap = true
    }

		this.$refs.map.$mapPromise.then(map => {
			// Deselect all markers when the map is clicked.
			map.addListener('click', () => this.unclickMarkers());

			this.resizeBounds();

      this.$nextTick(() => {
        setTimeout(() => {
          if (this.fullMap) {

            if(!notDesktop && window.innerWidth < 1024) {
              map.setZoom(8)
            } else if(!notDesktop && window.innerWidth >= 1400) {
              map.setZoom(10)
            }
          }
        }, 50);
      })
		});

		// Resize map on mobile depending on accordion size.
		var mapSection = document.querySelector('section.map');
		var mapElement = document.querySelector('.map-container');
		var accordionWrapperElement = this.$refs.accordions;
		var resizeObserver = new ResizeObserver(entries => {
			for(var entry of entries) {
				var height = entry.target.offsetHeight;
				if(notDesktop) {
					mapElement.style.marginTop = `-${height}px`;
					mapElement.style.height = `calc(100vh + ${height / 2}px)`;
				}
				// mapSection.style.height = notDesktop ? `calc(${height}px + 70vh)` : `calc(${height}px + 90vh - 747px)`;
				// mapElement.style.height = notDesktop ? `calc(${height}px + 70vh)` : `calc(${height}px + 80vh - 747px)`;
			}
		});
		resizeObserver.observe(accordionWrapperElement);
	},

	methods: {
		// Selects location based off its id.
		clickMarker(id) {
			this.unclickMarkers();
			var location = this.locations.find(location => location.id == id);
			if(location) {
				location.isActive = true;
				this.selectedArea = location.area;
				this.resizeBounds();
			}
		},

		// Deselects all locations.
		unclickMarkers() {
			for (var i = this.locations.length - 1; i >= 0; i--) {
				this.locations[i].isActive = false;
			}
		},

		// Icon state matrix, returns the correct icon given its selected/lab status.
		getLocationIcon(isActive, isLab) {
			if (isLab) return '/assets/images/icons/pin-brown.svg';
			if (!isLab) return '/assets/images/icons/pin.svg';
		},

		// Event listener for clicking on an accordion, if the accordion clicked is already opened then close it.
		areaClicked(area) {
			this.unclickMarkers();
			if(area && area == this.selectedArea) {
				this.selectedArea = 'all';
			} else {
				this.selectedArea = area;
			}
		},

		// Every time a region (or a location on mobile) is selected or deselected, pan/zoom to the appropriate area.
		resizeBounds() {
			var panX = 0;
			if(!notDesktop && window.innerWidth < 1024) {
				panX = -200;
			} else if(!notDesktop && window.innerWidth < 1400) {
				panX = 0;
			} else if(!notDesktop && window.innerWidth >= 1400) {
				panX = 0;
			}

			var panY = 0;
			if (notDesktop) {
				// The accordion heights are variable on screen widths less than 1200, therefore our panning needs to be flexible to get the info windows always visible.
				panY = (this.$refs.accordions.offsetHeight * -1) + 200;
			}

			var bounds = this.google && new this.google.maps.LatLngBounds();
			var positions = [];

			if(this.selectedArea == 'all') {
				// Hard coded these 4 corners due to not having the optimal ratio when bounds are resized to fit all locations.
				positions = [
					{ lat: 41.07244579862727, lng: -74.17649040474922 },
					{ lat: 40.56249280355302, lng: -74.25820353102561 },
					{ lat: 41.12688284439133, lng: -72.83710568274043 },
					{ lat: 40.62903527213518, lng: -72.73644458515355 },
				];
				// Uncomment this next line if you want the map to dynamically generate the map bounds.
				// positions = this.locations.map(location => location.position);
				// Alternatively, exclude international locations.
				// positions = this.locations.filter(location => !location.isInternational).map(location => location.position);
			} else {
				if(this.activeMarker !== null) {
					positions.push(this.activeMarker.position);
				} else {
					positions = this.activeArea.map(location => location.position);
				}
			}

			for (var i = positions.length - 1; i >= 0; i--) {
				bounds.extend(positions[i]);
			}

			// Don't zoom in too far on only one marker
			// https://stackoverflow.com/a/5345708
			if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
				var extendPoint1 = this.google && new this.google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01);
				var extendPoint2 = this.google && new this.google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.01);
				bounds.extend(extendPoint1);
				bounds.extend(extendPoint2);
			}

			this.$refs.map.$mapPromise.then(map => {
				map.fitBounds(bounds);
				if(positions.length > 1) {
					map.setZoom(map.getZoom() - 1);
				}
				map.panBy(panX, panY);
			});
		}
	},

	computed: {
		google: gmapApi,

		activeMarker() {
			return this.locations.find(marker => marker.isActive) || null;
		},

		activeArea() {
			return this.locations.filter(location => location.area == this.selectedArea);
		},

		areas() {
			var locations = this.locations;

			// I could have done a crazy logic one liner but that wouldn't be very nice to the next guy working on this.
			if(this.initialSelectedArea == 'International') {
				// Always show International accordion on the International Region page.
			} else if(notDesktop) {
				// Always hide International accordion on mobile.
				locations = this.locations.filter(location => !location.isInternational);
			} else if(this.initialSelectedArea == 'all') {
				// Always show International accordion on the Find a Location page (desktop only).
			} else {
				// Hide International accordion on all other pages.
				locations = this.locations.filter(location => !location.isInternational);
			}

			// Maps all locations to an object keyed by area name.
			return locations.reduce((group, location) => {
				group[location.area] = group[location.area] || [];
				group[location.area].push(location);
				return group;
			}, {});
		}
	},

	watch: {
		// Any time an accordion or pin is clicked on the area selected changes so we resize the bounds to fit that area.
		selectedArea(newValue, oldValue) {
			this.resizeBounds();
		}
	}

}
</script>